import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, CircleDashed, FileCheck2, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import JobPreviewDialog from './JobPreviewDialog';
import MonthShortcuts from './MonthShortcuts';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Parses a date-only string as a LOCAL date (not UTC midnight) — see
// ReferenceListEditor.jsx for the same fix and rationale. Always dd/mm/yyyy.
function formatDateOnly(isoDateStr) {
  if (!isoDateStr) return null;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

export default function CompletedJobsPanel() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [startingInvoiceNumber, setStartingInvoiceNumber] = useState('');
  const [jobs, setJobs] = useState([]);
  const [registerUploaded, setRegisterUploaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [auditorFilter, setAuditorFilter] = useState('all');
  const datesEditedRef = React.useRef(false);
  const jobsRequestIdRef = React.useRef(0);

  const load = useCallback(async (from, to, startingNum) => {
    const requestId = ++jobsRequestIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (startingNum) params.starting_invoice_number = startingNum;
      const { data } = await axios.get(`${API}/api/sales-invoicing/jobs`, { params, headers: authHeaders() });
      if (requestId !== jobsRequestIdRef.current) return; // a newer request has since started — this response is stale
      // Only fill the From/To inputs from the current-month default on the
      // very first (unprompted) load — if the user has already started
      // typing their own range, a late-arriving response must not stomp it.
      if (!datesEditedRef.current) {
        setFromDate(data.from_date);
        setToDate(data.to_date);
      }
      setJobs(data.jobs || []);
      setRegisterUploaded(!!data.register_uploaded);
    } catch (err) {
      if (requestId !== jobsRequestIdRef.current) return;
      // Clear the table on failure — leaving the last successful result
      // on screen would disagree with the (invalid) dates now shown.
      setJobs([]);
      toast.error(getApiError(err, 'Failed to load completed jobs.'));
    } finally {
      if (requestId === jobsRequestIdRef.current) setLoading(false);
    }
  }, []);

  // Initial load: no dates yet — backend fills in the current-month default.
  useEffect(() => { load(undefined, undefined, undefined); }, [load]);

  const editDate = (setter) => (e) => {
    datesEditedRef.current = true;
    setter(e.target.value);
  };

  const invalidStartingNumber = startingInvoiceNumber !== '' && !(Number.isInteger(Number(startingInvoiceNumber)) && Number(startingInvoiceNumber) > 0);

  const applyFilters = () => {
    datesEditedRef.current = true;
    const n = parseInt(startingInvoiceNumber, 10);
    load(fromDate, toDate, Number.isInteger(n) && n > 0 ? n : undefined);
  };
  const applyShortcut = (from, to) => {
    datesEditedRef.current = true;
    setFromDate(from);
    setToDate(to);
    const n = parseInt(startingInvoiceNumber, 10);
    load(from, to, Number.isInteger(n) && n > 0 ? n : undefined);
  };

  // Status/Site/Auditor filters apply to the already-loaded date range
  // client-side — no extra request, since the full list for a range is
  // already in memory.
  const siteOptions = [...new Set(jobs.map((j) => j.hotel_name).filter(Boolean))].sort();
  const auditorOptions = [...new Set(jobs.flatMap((j) => (j.auditor_names || '').split(',').map((n) => n.trim()).filter(Boolean)))].sort();

  const jobStatus = (j) => (j.decision_needed ? 'decision_needed' : j.needs_check_in_qbo ? 'needs_check_in_qbo' : 'mapped');

  const visibleJobs = jobs.filter((j) => {
    if (statusFilter === 'already_exported' && !j.already_exported) return false;
    if (statusFilter !== 'all' && statusFilter !== 'already_exported' && jobStatus(j) !== statusFilter) return false;
    if (siteFilter !== 'all' && j.hotel_name !== siteFilter) return false;
    if (auditorFilter !== 'all' && !(j.auditor_names || '').split(',').map((n) => n.trim()).includes(auditorFilter)) return false;
    return true;
  });

  return (
    <div data-testid="completed-jobs-panel">
      <div className="rounded-lg border border-sky-200 bg-sky-50 text-sky-900 text-sm px-4 py-3 mb-4" data-testid="completed-jobs-scope-notice">
        Browsing only — selecting a job previews its mapping status. No mapping is approved, no invoice number is reserved, and nothing is created or exported here.
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="completed-jobs-filters">
        <div>
          <Label htmlFor="ci-from-date" className="text-xs text-slate-500">From</Label>
          <Input id="ci-from-date" type="date" value={fromDate} onChange={editDate(setFromDate)} data-testid="completed-jobs-from-date" className="w-[160px]" />
        </div>
        <div>
          <Label htmlFor="ci-to-date" className="text-xs text-slate-500">To</Label>
          <Input id="ci-to-date" type="date" value={toDate} onChange={editDate(setToDate)} data-testid="completed-jobs-to-date" className="w-[160px]" />
        </div>
        <div>
          <Label htmlFor="ci-starting-invoice" className="text-xs text-slate-500">Manual starting invoice number</Label>
          <Input
            id="ci-starting-invoice"
            type="number"
            min="1"
            placeholder="e.g. 1042"
            value={startingInvoiceNumber}
            onChange={(e) => setStartingInvoiceNumber(e.target.value)}
            data-testid="completed-jobs-starting-invoice-number"
            className="w-[200px]"
          />
          {invalidStartingNumber && (
            <p className="text-xs text-red-600 mt-1" data-testid="completed-jobs-starting-invoice-error">
              Must be a whole number greater than 0 — ignored until then.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={applyFilters}
          data-testid="completed-jobs-apply-filters-btn"
          className="h-9 px-4 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 transition-colors"
        >
          Apply
        </button>
        <MonthShortcuts onSelect={applyShortcut} testIdPrefix="completed-jobs" />
        <span className="text-xs text-slate-400">
          {startingInvoiceNumber ? 'Preview numbers shown below — nothing is reserved.' : 'Enter a starting number to preview sequential invoice numbers.'}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="completed-jobs-secondary-filters">
        <div>
          <Label htmlFor="ci-status-filter" className="text-xs text-slate-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="ci-status-filter" className="w-[200px]" data-testid="completed-jobs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="decision_needed">Decision needed</SelectItem>
              <SelectItem value="needs_check_in_qbo">Mapped — Check in QBO</SelectItem>
              <SelectItem value="mapped">Mapped</SelectItem>
              <SelectItem value="already_exported">Already exported</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ci-site-filter" className="text-xs text-slate-500">Site</Label>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger id="ci-site-filter" className="w-[220px]" data-testid="completed-jobs-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {siteOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ci-auditor-filter" className="text-xs text-slate-500">Auditor</Label>
          <Select value={auditorFilter} onValueChange={setAuditorFilter}>
            <SelectTrigger id="ci-auditor-filter" className="w-[200px]" data-testid="completed-jobs-auditor-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All auditors</SelectItem>
              {auditorOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(statusFilter !== 'all' || siteFilter !== 'all' || auditorFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setStatusFilter('all'); setSiteFilter('all'); setAuditorFilter('all'); }}
            data-testid="completed-jobs-clear-secondary-filters"
            className="text-xs text-slate-500 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {!registerUploaded && (
        <div className="text-xs text-slate-400 mb-3" data-testid="completed-jobs-no-register-note">
          No QBO invoice register has been uploaded yet, so "Possible QBO match" cannot be checked and will show as no match for every job.
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Auditors</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Preview #</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center text-slate-500">Loading…</TableCell></TableRow>}
            {!loading && visibleJobs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8" data-testid="completed-jobs-empty">
                <SearchX className="h-5 w-5 mx-auto mb-1 text-slate-300" />
                {jobs.length === 0 ? 'No completed jobs in this range.' : 'No jobs match the current filters.'}
              </TableCell></TableRow>
            )}
            {!loading && visibleJobs.map((j) => (
              <TableRow
                key={j.job_id}
                data-testid={`completed-job-row-${j.job_id}`}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => setSelectedJobId(j.job_id)}
              >
                <TableCell className="font-medium">{j.hotel_name || '—'}</TableCell>
                <TableCell className="text-sm text-slate-500">{formatDateOnly(j.scheduled_date)}</TableCell>
                <TableCell className="text-sm text-slate-500">{j.auditor_names || '—'}</TableCell>
                <TableCell>
                  {j.region ? (
                    <Badge variant="secondary" className="whitespace-nowrap">{j.region}</Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-400 text-amber-700 whitespace-nowrap" data-testid={`completed-job-region-unresolved-${j.job_id}`}>
                      Region unresolved
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {j.decision_needed ? (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid={`completed-job-decision-badge-${j.job_id}`}>
                        <CircleDashed className="h-3 w-3 mr-1" />Decision needed
                      </Badge>
                    ) : j.needs_check_in_qbo ? (
                      <Badge variant="secondary" className="bg-sky-100 text-sky-800" data-testid={`completed-job-decision-badge-${j.job_id}`} title="Mapped locally but not yet confirmed active in QBO.">
                        <CheckCircle2 className="h-3 w-3 mr-1" />Mapped — Check in QBO
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-600" data-testid={`completed-job-decision-badge-${j.job_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" />Mapped
                      </Badge>
                    )}
                    {j.already_exported && (
                      <Badge className="bg-slate-700" data-testid={`completed-job-exported-badge-${j.job_id}`} title={`Invoice #${j.already_exported_detail?.invoice_number ?? '?'}`}>
                        <FileCheck2 className="h-3 w-3 mr-1" />Already exported
                      </Badge>
                    )}
                    {j.possible_qbo_match && (
                      <Badge className="bg-orange-500" data-testid={`completed-job-possible-match-badge-${j.job_id}`} title={`Invoice #${j.possible_qbo_match_detail?.invoice_number ?? '?'}`}>
                        Possible QBO match
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm text-slate-500">{j.preview_invoice_number ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedJobId && (
        <JobPreviewDialog jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
      )}
    </div>
  );
}
